// Serviço de cache simplificado para fotos usando localStorage
export interface PhotoAnalysisData {
  fileIndex: number;
  fileName: string;
  fileSize: number;
  lastModified: number;
  isAnalyzed: boolean;
  analysisDate?: string;
  cameraModel?: string;
  resolution?: string;
  author?: string;
  captureData?: string;
  width?: number;
  height?: number;
  // Campos manuais editáveis
  bibNumbers?: string[];
  eventId?: string; // ID do evento associado
}

export class PhotoCacheService {
  private static readonly CACHE_KEY = 'fotup_photo_analysis_cache';

  // Criar cache inicial com arquivos selecionados
  static createInitialCache(files: File[], photographerId: string): void {
    try {
      const analysisData: PhotoAnalysisData[] = files.map((file, index) => ({
        fileIndex: index,
        fileName: file.name,
        fileSize: file.size,
        lastModified: file.lastModified,
        isAnalyzed: false,
        bibNumbers: []
      }));

      localStorage.setItem(this.CACHE_KEY, JSON.stringify(analysisData));
      console.log('📦 Cache inicial criado com', files.length, 'arquivos');
    } catch (error) {
      console.error('❌ Erro ao criar cache inicial:', error);
    }
  }

  // Obter todos os dados de análise
  static getAllAnalysisData(): PhotoAnalysisData[] {
    try {
      const cacheData = localStorage.getItem(this.CACHE_KEY);
      if (cacheData) {
        return JSON.parse(cacheData);
      }
      return [];
    } catch (error) {
      console.error('❌ Erro ao obter dados de análise:', error);
      return [];
    }
  }

  // Obter dados de análise por índice
  static getAnalysisDataByIndex(fileIndex: number): PhotoAnalysisData | null {
    try {
      const allData = this.getAllAnalysisData();
      return allData.find(data => data.fileIndex === fileIndex) || null;
    } catch (error) {
      console.error('❌ Erro ao obter dados por índice:', error);
      return null;
    }
  }

  // Atualizar dados de análise após PyImg
  static updateAnalysisData(fileIndex: number, pyimgResult: any): void {
    try {
      const allData = this.getAllAnalysisData();
      const dataIndex = allData.findIndex(data => data.fileIndex === fileIndex);
      
      if (dataIndex !== -1) {
        // Preservar dados manuais existentes
        const existingData = allData[dataIndex];
        
        allData[dataIndex] = {
          ...existingData, // Preservar dados manuais (bibNumbers, tags, eventIds)
          isAnalyzed: true,
          analysisDate: new Date().toISOString(),
          cameraModel: pyimgResult.technical.camera || pyimgResult.technical.camera_make || '',
          resolution: `${pyimgResult.technical.width}x${pyimgResult.technical.height}`,
          author: pyimgResult.technical.author || '',
          captureData: pyimgResult.technical.capture_datetime || '',
          width: pyimgResult.technical.width,
          height: pyimgResult.technical.height
        };

        localStorage.setItem(this.CACHE_KEY, JSON.stringify(allData));
      }
    } catch (error) {
      console.error('❌ Erro ao atualizar dados de análise:', error);
    }
  }

  // Atualizar dados manuais (bibNumbers)
  static updateManualData(fileIndex: number, manualData: { bibNumbers?: string[] }): void {
    try {
      const allData = this.getAllAnalysisData();
      const dataIndex = allData.findIndex(data => data.fileIndex === fileIndex);
      
      if (dataIndex !== -1) {
        allData[dataIndex] = {
          ...allData[dataIndex],
          ...manualData
        };

        localStorage.setItem(this.CACHE_KEY, JSON.stringify(allData));
      }
    } catch (error) {
      console.error('❌ Erro ao atualizar dados manuais:', error);
    }
  }

  // Verificar se arquivo foi analisado
  static isFileAnalyzed(fileIndex: number): boolean {
    try {
      const data = this.getAnalysisDataByIndex(fileIndex);
      return data?.isAnalyzed || false;
    } catch (error) {
      console.error('❌ Erro ao verificar se arquivo foi analisado:', error);
      return false;
    }
  }

  // Limpar cache completamente
  static clearCache(): void {
    try {
      localStorage.removeItem(this.CACHE_KEY);
      console.log('🗑️ Cache limpo');
    } catch (error) {
      console.error('❌ Erro ao limpar cache:', error);
    }
  }

  // Obter estatísticas do cache
  static getCacheStats(): { totalFiles: number; analyzedFiles: number; cacheSize: string } {
    try {
      const allData = this.getAllAnalysisData();
      const totalFiles = allData.length;
      const analyzedFiles = allData.filter(data => data.isAnalyzed).length;
      const cacheSize = new Blob([JSON.stringify(allData)]).size;
      
      return {
        totalFiles,
        analyzedFiles,
        cacheSize: `${(cacheSize / 1024).toFixed(2)} KB`
      };
    } catch (error) {
      console.error('❌ Erro ao obter estatísticas do cache:', error);
      return { totalFiles: 0, analyzedFiles: 0, cacheSize: '0 KB' };
    }
  }

  // Verificar se cache existe
  static hasCache(): boolean {
    try {
      const cacheData = localStorage.getItem(this.CACHE_KEY);
      return cacheData !== null;
    } catch (error) {
      console.error('❌ Erro ao verificar se cache existe:', error);
      return false;
    }
  }

  // Setar eventId em todos os itens do cache
  static setEventId(eventId: string): void {
    try {
      const allData = this.getAllAnalysisData();
      const updatedData = allData.map(data => ({
        ...data,
        eventId
      }));
      localStorage.setItem(this.CACHE_KEY, JSON.stringify(updatedData));
      console.log('📦 EventId definido no cache:', eventId);
    } catch (error) {
      console.error('❌ Erro ao definir eventId no cache:', error);
    }
  }

  // Obter eventId do cache
  static getEventId(): string | null {
    try {
      const allData = this.getAllAnalysisData();
      if (allData.length > 0 && allData[0].eventId) {
        return allData[0].eventId;
      }
      return null;
    } catch (error) {
      console.error('❌ Erro ao obter eventId do cache:', error);
      return null;
    }
  }
}

export default PhotoCacheService;